import {useState, useEffect} from "react";
import Helmet from "react-helmet";

import {getCachedLinksIDMap} from "../../hooks/useStoryblokLinks";
import {getPathnameWithoutLocale} from "../../helpers/URLHelpers";
import {useIntl} from "react-intl";
import {getLanguageByBasename} from "../../helpers/routingHelpers";
import {LANGUAGES} from "../../constants/languages";
import {baseSiteUrl} from "../../../config";

const DEFAULT_HOST = "https://www.crunchyroll.com";
const getInitialAlternates = () => {
  const pathname = getPathnameWithoutLocale();

  return LANGUAGES.filter(({basename}) => Boolean(basename)).map(
    ({basename}) => ({
      lang: basename,
      href: `${baseSiteUrl}${basename ? `/${basename}` : ""}${pathname}`,
    })
  );
};

const getSeoTitle = title => {
  const language = getLanguageByBasename().key;
  const {formatMessage} = useIntl();

  switch (language) {
    case "es-419":
    case "es-ES":
      return `${title} - Crunchyroll Noticias`;
    case "pt-BR":
    case "pt-PT":
      return `${title} - Crunchyroll Notícias`;
    default:
      return formatMessage({id: "seo.content.title"}, {title});
  }
};

function HelmetSEO({
  lang,
  seo_data,
  fallback_title,
  fallback_description,
  fallback_image_url,
  story_alternates,
  initialAlternates = getInitialAlternates(),
}) {
  const [alternates, setAlternates] = useState(initialAlternates);

  let actualLang = lang !== "default" ? lang : "en";
  let title = seo_data.title || fallback_title,
    description = seo_data.description || fallback_description;
  let ogTitle = seo_data.og_title || title,
    ogDescription = seo_data.og_description || description,
    ogImageUrl = seo_data.og_image || fallback_image_url;
  let twtitle = seo_data.twitter_title || ogTitle,
    twDescription = seo_data.twitter_description || ogDescription,
    twImageUrl = seo_data.twitter_image || ogImageUrl;

  // Multi Language Stuff
  let host = DEFAULT_HOST;
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.origin
  ) {
    host = window.origin;
  }

  useEffect(() => {
    if (story_alternates) {
      getCachedLinksIDMap().then(links => {
        setAlternates(
          story_alternates.map(alt => {
            const alternateUrl = links[alt.id]?.real_path;
            const aLang = alternateUrl.substring(1);

            return {
              href: host + alternateUrl,
              lang: aLang.substring(0, aLang.indexOf("/")),
            };
          })
        );
      });
    }
  }, [story_alternates]);

  return (
    <Helmet>
      <html lang={actualLang} />
      {title ? <title>{getSeoTitle(title)}</title> : null}
      {<link rel="canonical" href={host + getPathnameWithoutLocale()} />}
      {actualLang ? (
        <link
          rel="alternate"
          href={host + getPathnameWithoutLocale()}
          hrefLang={actualLang}
        />
      ) : null}
      {alternates.map(alternate => (
        <link
          key={alternate.lang}
          rel="alternate"
          href={alternate.href}
          hrefLang={alternate.lang}
        />
      ))}

      {description ? <meta name="description" content={description} /> : null}
      {description ? (
        <meta property="description" content={description} />
      ) : null}

      {/* Open Graph */}
      {ogTitle ? <meta name="og:title" content={ogTitle} /> : null}
      {ogTitle ? <meta property="og:title" content={ogTitle} /> : null}

      {ogDescription ? (
        <meta name="og:description" content={ogDescription} />
      ) : null}
      {ogDescription ? (
        <meta property="og:description" content={ogDescription} />
      ) : null}

      {ogImageUrl ? <meta name="og:image" content={ogImageUrl} /> : null}
      {ogImageUrl ? <meta property="og:image" content={ogImageUrl} /> : null}

      {/* Twitter */}
      {twtitle ? <meta name="twitter:title" content={twtitle} /> : null}

      {twDescription ? (
        <meta name="twitter:description" content={twDescription} />
      ) : null}

      {twImageUrl ? <meta name="twitter:image" content={twImageUrl} /> : null}

      <meta name="robots" content="max-image-preview:large" />
    </Helmet>
  );
}

export default HelmetSEO;
